import { createJsonClient } from '../../../shared/httpClient';
import { payrollConfigApiBaseUrl, paymentProcessingApiBaseUrl } from '../../../shared/env';
import { ApiError } from '../../identity/types';
import { apiErrorFromResponse } from '../../../shared/apiErrorFromResponse';
import type {
  AssignableRoleResponse,
  CompanyAddressResponse,
  CompanyLogoResponse,
  CompanyVerificationDocumentResponse,
  CompanyVerificationStatusResponse,
  CreateFundingAccountRequest,
  CreateInvitationRequest,
  CreateOwnerRequest,
  CreatePayScheduleRequest,
  CreateRegistrationRequest,
  FinalizeRegistrationResponse,
  FundingAccountResponse,
  InvitationResponse,
  InvitationValidityResponse,
  OwnerResponse,
  PayScheduleResponse,
  RegistrationCreatedResponse,
  RegistrationStatusResponse,
  UpsertCompanyAddressRequest,
} from '../types';

// Identity.Api itself — same-origin relative path, matching HttpIdentityGateway's own base-URL choice
// (see that file's comment on SameSite=Strict cookies requiring same-origin requests).
const identityRequest = createJsonClient('');
const payrollConfigRequest = createJsonClient(payrollConfigApiBaseUrl);
const paymentProcessingRequest = createJsonClient(paymentProcessingApiBaseUrl);

/** Raw multipart upload, bypassing the JSON client (which always sets Content-Type: application/json) —
 * mirrors the one existing multipart upload in this codebase, reports/DocumentsPage.tsx's own pattern. */
async function uploadFile<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);

  const response = await fetch(path, { method: 'POST', credentials: 'include', body: form });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, `Upload to ${path} failed with ${response.status}`);
  }
  return (await response.json()) as T;
}

export const adminSignupGateway = {
  // ---- Step 1 ----
  createRegistration: (request: CreateRegistrationRequest) =>
    identityRequest<RegistrationCreatedResponse>('/api/registrations', { method: 'POST', body: JSON.stringify(request) }),

  // ---- Status / resume ----
  getStatus: () => identityRequest<RegistrationStatusResponse>('/api/registrations/status'),

  // ---- Step 2: Address ----
  getAddress: () => identityRequest<CompanyAddressResponse | null>('/api/registrations/address').catch((err) => {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }),
  saveAddress: (request: UpsertCompanyAddressRequest) =>
    identityRequest<CompanyAddressResponse>('/api/registrations/address', { method: 'POST', body: JSON.stringify(request) }),

  // ---- Step 1B: Identity Verification ----
  getVerification: () => identityRequest<CompanyVerificationStatusResponse>('/api/registrations/verification'),
  setDocumentType: (documentType: string, missingDocumentReason: string | null) =>
    identityRequest<CompanyVerificationStatusResponse>('/api/registrations/verification/document-type', {
      method: 'POST',
      body: JSON.stringify({ documentType, missingDocumentReason }),
    }),
  uploadDocument: (file: File) => uploadFile<CompanyVerificationDocumentResponse>('/api/registrations/verification/documents', file),
  uploadLivePhoto: (file: File) => uploadFile<CompanyVerificationStatusResponse>('/api/registrations/verification/photo', file),
  skipVerification: () => identityRequest<CompanyVerificationStatusResponse>('/api/registrations/verification/skip', { method: 'POST' }),

  // ---- Partner Sign Up only (EPN-35) — Admin Sign Up never calls this ----
  uploadLogo: (file: File) => uploadFile<CompanyLogoResponse>('/api/registrations/logo', file),

  // ---- Step 3: Banking + Pay Schedule (two different services, in the required order) ----
  savePaySchedule: (request: CreatePayScheduleRequest) =>
    payrollConfigRequest<PayScheduleResponse>('/api/pay-schedules/onboarding', { method: 'POST', body: JSON.stringify(request) }),
  getActivePaySchedule: () =>
    payrollConfigRequest<PayScheduleResponse | null>('/api/pay-schedules/onboarding/active').catch((err) => {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }),
  saveFundingAccount: (request: CreateFundingAccountRequest) =>
    paymentProcessingRequest<FundingAccountResponse>('/api/funding-accounts/onboarding', { method: 'POST', body: JSON.stringify(request) }),
  getFundingAccount: () =>
    paymentProcessingRequest<FundingAccountResponse | null>('/api/funding-accounts/onboarding').catch((err) => {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }),

  // ---- Step 4: Admin Information ----
  createOwner: (request: CreateOwnerRequest) =>
    identityRequest<OwnerResponse>('/api/registrations/owner', { method: 'POST', body: JSON.stringify(request) }),
  listAssignableRoles: () => identityRequest<AssignableRoleResponse[]>('/api/registrations/roles'),
  createInvitation: (request: CreateInvitationRequest) =>
    identityRequest<InvitationResponse>('/api/registrations/invitations', { method: 'POST', body: JSON.stringify(request) }),
  listInvitations: () => identityRequest<InvitationResponse[]>('/api/registrations/invitations'),
  revokeInvitation: (invitationId: string) =>
    identityRequest<void>(`/api/registrations/invitations/${invitationId}`, { method: 'DELETE' }),

  // ---- Finalize ----
  finalize: () => identityRequest<FinalizeRegistrationResponse>('/api/registrations/finalize', { method: 'POST' }),

  // ---- Public invitation acceptance (no onboarding/session cookie involved at all) ----
  checkInvitation: (token: string) => identityRequest<InvitationValidityResponse>(`/api/memberships/invitations/${token}`),
  // Both displayName and password are omitted (null) for an invitee who already has an EPN account — this
  // acceptance is membership-only and must never send a value implying it changes their existing global
  // profile/credential; see AcceptInvitationPage.tsx's own doc comment on why that branch never collects
  // either field.
  acceptInvitation: (token: string, displayName: string | null, password: string | null) =>
    identityRequest<void>(`/api/memberships/invitations/${token}/accept`, {
      method: 'POST',
      body: JSON.stringify({ displayName, password }),
    }),
};
